/*
 * Ad conversion events, sent through the gtag.js already loaded in index.html.
 *
 * Each conversion is a GA4 event (mark it a key event in GA4, then import it
 * into Google Ads). If a Google Ads conversion label is configured
 * (VITE_GADS_TRIAL_SEND_TO / VITE_GADS_DEMO_SEND_TO, e.g. "AW-123/AbC"), the
 * same conversion is also sent to Ads directly. Like onboardingTracker, every
 * call is fire-and-forget and must never break the page.
 */
import { findPlan } from "../data/onboarding.js";

// Worth far less than a trial (Starter is R399/month); just enough for Google
// to prefer clicks that go on to open the demo.
export const DEMO_VALUE_ZAR = 40;

const env = import.meta.env || {};
const FIRED_KEY = "buzztracker_conversions_fired";

export function trialStartedCalls({ plan, period, ref, adsSendTo = "" }) {
  const value = findPlan(plan)?.priceMonthly || 0;
  const calls = [
    [
      "event",
      "trial_started",
      { value, currency: "ZAR", plan, period, transaction_id: ref },
    ],
  ];
  if (adsSendTo) {
    calls.push([
      "event",
      "conversion",
      { send_to: adsSendTo, value, currency: "ZAR", transaction_id: ref },
    ]);
  }
  return calls;
}

export function demoStartedCalls({ industry, adsSendTo = "" }) {
  const calls = [
    [
      "event",
      "demo_started",
      { value: DEMO_VALUE_ZAR, currency: "ZAR", industry },
    ],
  ];
  if (adsSendTo) {
    calls.push([
      "event",
      "conversion",
      { send_to: adsSendTo, value: DEMO_VALUE_ZAR, currency: "ZAR" },
    ]);
  }
  return calls;
}

// One fire per key per browser tab, so a refresh or back-navigation can't
// double-count. Returns false when the key has already fired.
function firstTime(key) {
  try {
    const fired = JSON.parse(sessionStorage.getItem(FIRED_KEY) || "[]");
    if (fired.includes(key)) return false;
    sessionStorage.setItem(FIRED_KEY, JSON.stringify([...fired, key]));
  } catch {
    // sessionStorage unavailable — fire anyway; GA/Ads dedupe on transaction_id.
  }
  return true;
}

function send(calls) {
  try {
    if (typeof window.gtag !== "function") return;
    calls.forEach((args) => window.gtag(...args));
  } catch {
    // Never let tracking surface an error.
  }
}

/** Google Ads needs its own tag configured before conversions can be sent to it. */
export function initAdsTag() {
  if (env.VITE_GADS_ID) send([["config", env.VITE_GADS_ID]]);
}

/** Onboarding finished and the trial workspace exists. `ref` is the onboarding session. */
export function trackTrialStarted({ plan, period, ref }) {
  if (!firstTime(`trial:${ref}`)) return;
  send(
    trialStartedCalls({
      plan,
      period,
      ref,
      adsSendTo: env.VITE_GADS_TRIAL_SEND_TO || "",
    }),
  );
}

/** A visitor opened a demo workspace. */
export function trackDemoStarted({ industry }) {
  if (!firstTime("demo")) return;
  send(
    demoStartedCalls({ industry, adsSendTo: env.VITE_GADS_DEMO_SEND_TO || "" }),
  );
}
